import React, { useState, useEffect, useRef } from 'react';
import { practiceService, availabilityService, dateTimeHelpers } from '../../models/database';
import { PlacesAutocomplete, parsePlaceToAddress } from '../../utils/googlePlacesApi';
import { importSamplePractices } from '../../utils/importData';

const PracticeManagement = () => {
  const [practices, setPractices] = useState([]);
  const [practiceAvailability, setPracticeAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPractice, setEditingPractice] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importData, setImportData] = useState('');
  
  // State for raw services input
  const [servicesInput, setServicesInput] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      postcode: '',
      latitude: null,
      longitude: null,
    },
    contact: {
      phone: '',
      email: '',
      website: '',
    },
    services: [],
    openingHours: {
      monday: { open: '09:00', close: '17:00', isClosed: false },
      tuesday: { open: '09:00', close: '17:00', isClosed: false },
      wednesday: { open: '09:00', close: '17:00', isClosed: false },
      thursday: { open: '09:00', close: '17:00', isClosed: false },
      friday: { open: '09:00', close: '17:00', isClosed: false },
      saturday: { open: '10:00', close: '14:00', isClosed: false },
      sunday: { open: '10:00', close: '14:00', isClosed: true },
    }
  });
  
  // State to track if Google Places API is available
  const [placesApiAvailable, setPlacesApiAvailable] = useState(true);
  const [placesApiError, setPlacesApiError] = useState(null);
  
  // State for the formatted address (for editing)
  const [formattedAddress, setFormattedAddress] = useState('');
  
  // Update formatted address when editing a practice
  useEffect(() => {
    if (editingPractice) {
      const address = [
        formData.address.line1,
        formData.address.line2,
        formData.address.city,
        formData.address.postcode
      ].filter(Boolean).join(', ');
      
      setFormattedAddress(address);
    } else {
      setFormattedAddress('');
    }
  }, [editingPractice, formData.address]);
  
  // Handle place selection from Google Places Autocomplete
  const handlePlaceSelected = (place) => {
    if (!place.geometry) {
      console.error('No geometry found for the selected place');
      return;
    }
    
    console.log('Place selected in PracticeManagement:', place);
    
    const parsedAddress = parsePlaceToAddress(place);
    console.log('Parsed address data:', parsedAddress);
    
    if (parsedAddress) {
      // Update form with business data
      setFormData(prev => ({
        ...prev,
        name: parsedAddress.name || prev.name, // Use business name if available
        address: {
          line1: parsedAddress.line1 || prev.address.line1,
          line2: parsedAddress.line2 || prev.address.line2,
          city: parsedAddress.city || prev.address.city,
          postcode: parsedAddress.postcode || prev.address.postcode,
          latitude: parsedAddress.latitude,
          longitude: parsedAddress.longitude,
        },
        contact: {
          ...prev.contact,
          phone: parsedAddress.phone || prev.contact.phone, // Use business phone if available
          website: parsedAddress.website || prev.contact.website, // Use business website if available
        }
      }));
      
      // If the place has coordinates, update the coords field
      if (parsedAddress.latitude && parsedAddress.longitude) {
        console.log('Setting coordinates from place:', parsedAddress.latitude, parsedAddress.longitude);
      }
    }
  };

  // Fetch practices on component mount
  useEffect(() => {
    fetchPractices();
  }, []);
  
  // Fetch next available appointments for all practices
  useEffect(() => {
    if (practices.length > 0) {
      fetchNextAvailableAppointments();
    }
  }, [practices]);
  
  // Format time for display (HH:MM AM/PM)
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  };
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };
  
  // Fetch next available appointment for each practice
  const fetchNextAvailableAppointments = async () => {
    try {
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      
      const availabilityData = {};
      
      // Check availability for the next 7 days for each practice
      for (const practice of practices) {
        let foundAvailability = false;
        
        for (let i = 0; i < 7; i++) {
          const checkDate = new Date(today);
          checkDate.setDate(today.getDate() + i);
          
          const year = checkDate.getFullYear();
          const month = checkDate.getMonth() + 1; // JavaScript months are 0-indexed
          const day = checkDate.getDate();
          
          const dateTimestamp = dateTimeHelpers.createDateTimestamp(year, month, day);
          
          try {
            const practiceAvailability = await availabilityService.getPracticeAvailability(practice.id, dateTimestamp);
            
            // Find the first available slot
            let firstAvailableSlot = null;
            
            for (const availability of practiceAvailability) {
              for (const slot of availability.slots) {
                if (!slot.isBooked) {
                  firstAvailableSlot = {
                    date: dateTimestamp,
                    startTime: slot.startTime,
                    endTime: slot.endTime
                  };
                  break;
                }
              }
              
              if (firstAvailableSlot) break;
            }
            
            if (firstAvailableSlot) {
              availabilityData[practice.id] = firstAvailableSlot;
              foundAvailability = true;
              break;
            }
          } catch (err) {
            console.error(`Error fetching availability for practice ${practice.id} on date ${dateTimestamp}:`, err);
          }
        }
        
        if (!foundAvailability) {
          availabilityData[practice.id] = null;
        }
      }
      
      setPracticeAvailability(availabilityData);
    } catch (err) {
      console.error('Error fetching next available appointments:', err);
    }
  };

  // Fetch all practices
  const fetchPractices = async () => {
    try {
      setLoading(true);
      const practicesData = await practiceService.getAllPractices();
      setPractices(practicesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching practices:', err);
      setError('Failed to load practices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData({
        ...formData,
        [section]: {
          ...formData[section],
          [field]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Handle services input (comma-separated list)
  const handleServicesChange = (e) => {
    setServicesInput(e.target.value);
  };

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      name: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        postcode: '',
        latitude: null,
        longitude: null,
      },
      contact: {
        phone: '',
        email: '',
        website: '',
      },
      services: [],
      openingHours: {
        monday: { open: '09:00', close: '17:00', isClosed: false },
        tuesday: { open: '09:00', close: '17:00', isClosed: false },
        wednesday: { open: '09:00', close: '17:00', isClosed: false },
        thursday: { open: '09:00', close: '17:00', isClosed: false },
        friday: { open: '09:00', close: '17:00', isClosed: false },
        saturday: { open: '10:00', close: '14:00', isClosed: false },
        sunday: { open: '10:00', close: '14:00', isClosed: true },
      }
    });
    setServicesInput('');
    setEditingPractice(null);
  };
  
  // Handle opening hours changes
  const handleOpeningHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: field === 'isClosed' ? !prev.openingHours[day].isClosed : value
        }
      }
    }));
  };

  // Handle form submission for adding/editing a practice
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Process services input
      const servicesArray = servicesInput
        .split(',')
        .map(service => service.trim())
        .filter(service => service !== '');
      
      // Use coordinates from the address if available, otherwise use default coordinates
      const coords = {
        lat: formData.address.latitude ? String(formData.address.latitude) : "51.338982485515686",
        lng: formData.address.longitude ? String(formData.address.longitude) : "-0.26289573933052635"
      };
      
      console.log('Using coordinates:', coords);
      
      const dataToSubmit = {
        ...formData,
        services: servicesArray,
        coords: coords
      };
      
      if (editingPractice) {
        // Update existing practice
        await practiceService.updatePractice(editingPractice.id, dataToSubmit);
      } else {
        // Add new practice
        await practiceService.createPractice(dataToSubmit);
      }
      
      // Refresh practices list
      await fetchPractices();
      
      // Reset form and UI state
      resetForm();
      setShowAddForm(false);
      setError(null);
    } catch (err) {
      console.error('Error saving practice:', err);
      setError('Failed to save practice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle practice deletion
  const handleDelete = async (practiceId) => {
    if (window.confirm('Are you sure you want to delete this practice?')) {
      try {
        setLoading(true);
        await practiceService.deletePractice(practiceId);
        await fetchPractices();
        setError(null);
      } catch (err) {
        console.error('Error deleting practice:', err);
        setError('Failed to delete practice. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle editing a practice
  const handleEdit = (practice) => {
    setFormData({
      name: practice.name,
      address: { 
        ...practice.address,
        latitude: practice.address.latitude || null,
        longitude: practice.address.longitude || null,
      },
      contact: { ...practice.contact },
      services: [...practice.services],
      openingHours: practice.openingHours || {
        monday: { open: '09:00', close: '17:00', isClosed: false },
        tuesday: { open: '09:00', close: '17:00', isClosed: false },
        wednesday: { open: '09:00', close: '17:00', isClosed: false },
        thursday: { open: '09:00', close: '17:00', isClosed: false },
        friday: { open: '09:00', close: '17:00', isClosed: false },
        saturday: { open: '10:00', close: '14:00', isClosed: false },
        sunday: { open: '10:00', close: '14:00', isClosed: true },
      }
    });
    setServicesInput(practice.services.join(', '));
    setEditingPractice(practice);
    setShowAddForm(true);
  };

  // Handle importing practices
  const handleImport = async () => {
    try {
      setLoading(true);
      
      // Parse the import data (assuming JSON format)
      let practicesData;
      try {
        practicesData = JSON.parse(importData);
        
        // Ensure it's an array
        if (!Array.isArray(practicesData)) {
          practicesData = [practicesData];
        }
      } catch (parseErr) {
        setError('Invalid JSON format. Please check your data and try again.');
        setLoading(false);
        return;
      }
      
      // Import the practices
      await practiceService.importPractices(practicesData);
      
      // Refresh practices list
      await fetchPractices();
      
      // Reset UI state
      setImportData('');
      setImportModalOpen(false);
      setError(null);
    } catch (err) {
      console.error('Error importing practices:', err);
      setError('Failed to import practices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Practice Management</h2>
        <div className="space-x-2">
          <button
            onClick={() => {
              resetForm();
              setShowAddForm(!showAddForm);
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            {showAddForm ? 'Cancel' : 'Add Practice'}
          </button>
          <button
            onClick={() => setImportModalOpen(true)}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
          >
            Import Practices
          </button>
          <button
            onClick={async () => {
              try {
                setLoading(true);
                await importSamplePractices();
                await fetchPractices();
                setError(null);
                setSuccess('Sample practices imported successfully!');
                setTimeout(() => setSuccess(null), 3000);
              } catch (err) {
                console.error('Error importing sample practices:', err);
                setError('Failed to import sample practices. Please try again.');
              } finally {
                setLoading(false);
              }
            }}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded"
          >
            Import Sample Data
          </button>
        </div>
      </div>
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Add/Edit Practice Form */}
      {showAddForm && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6 border">
          <h3 className="text-xl font-medium mb-4">
            {editingPractice ? 'Edit Practice' : 'Add New Practice'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Practice Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Practice Name*
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Services */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Services (comma-separated)
                </label>
                <input
                  type="text"
                  name="services"
                  value={servicesInput}
                  onChange={handleServicesChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Address Search */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Address*
                </label>
                {placesApiAvailable ? (
                  <>
                    <PlacesAutocomplete
                      onPlaceSelect={handlePlaceSelected}
                      containerClassName="relative"
                      suggestionsClassName="absolute z-10 w-full bg-white border border-gray-300 mt-1 rounded-md shadow-lg max-h-60 overflow-auto"
                      suggestionItemClassName="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      inputProps={{
                        placeholder: "Search for a dental practice or healthcare business...",
                        className: "w-full px-3 py-2 pl-10 border rounded-md focus:ring-blue-500 focus:border-blue-500",
                        defaultValue: formattedAddress
                      }}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" style={{ marginTop: '1.75rem' }}>
                      {/* <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg> */}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Type to search for a business or practice using Google Places (e.g., "dental practice in London")
                    </p>
                  </>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                    <p className="font-medium">Google Places API is not available</p>
                    
                    {placesApiError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mt-2 mb-3">
                        <p className="text-sm font-medium">Error details:</p>
                        <p className="text-sm whitespace-pre-line">{placesApiError}</p>
                      </div>
                    )}
                    
                    <p className="text-sm mt-1">
                      Please manually enter the address details below. To fix this issue, make sure you have:
                    </p>
                    <ul className="list-disc list-inside text-sm mt-1">
                      <li>Enabled the Maps JavaScript API in Google Cloud Console</li>
                      <li>Enabled the Places API in Google Cloud Console</li>
                      <li>Enabled the Geocoding API in Google Cloud Console</li>
                      <li>Added a valid API key to your .env file</li>
                      <li>Waited a few minutes for the API changes to propagate</li>
                      <li>Checked that the API key is from the same project where the APIs are enabled</li>
                      <li>Verified that billing is enabled for your Google Cloud project</li>
                      <li>Ensured there are no domain restrictions on your API key that would block localhost</li>
                    </ul>
                    
                    <div className="mt-3 text-sm">
                      <p className="font-medium">How to fix:</p>
                      <ol className="list-decimal list-inside mt-1">
                        <li>Go to the <a href="https://console.cloud.google.com/apis/dashboard" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a></li>
                        <li>Select your project</li>
                        <li>Go to "APIs & Services" {'->'} "Library"</li>
                        <li>Search for and enable: 
                          <ul className="list-disc list-inside ml-4 mt-1">
                            <li>Maps JavaScript API</li>
                            <li><strong>Places API</strong> - Google has updated the Places API</li>
                            <li>Geocoding API</li>
                          </ul>
                        </li>
                        <li>Go to "APIs & Services" {'->'} "Credentials"</li>
                        <li>Check your API key restrictions or create a new API key</li>
                        <li>Copy the API key to your .env file as REACT_APP_GOOGLE_MAPS_API_KEY</li>
                        <li>Restart your development server after making these changes</li>
                      </ol>
                    </div>
                    
                    <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                      <p className="font-medium text-blue-800">Important Note:</p>
                      <p className="mt-1">Google has completely replaced their old Places API with a new version. Our application has been updated to work with this new API. Make sure you enable the "Places API" in your Google Cloud Console.</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Address Line 1 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 1*
                </label>
                <input
                  type="text"
                  name="address.line1"
                  value={formData.address.line1}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Address Line 2 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 2
                </label>
                <input
                  type="text"
                  name="address.line2"
                  value={formData.address.line2}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City*
                </label>
                <input
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Postcode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postcode*
                </label>
                <input
                  type="text"
                  name="address.postcode"
                  value={formData.address.postcode}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone*
                </label>
                <input
                  type="tel"
                  name="contact.phone"
                  value={formData.contact.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email*
                </label>
                <input
                  type="email"
                  name="contact.email"
                  value={formData.contact.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  name="contact.website"
                  value={formData.contact.website}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
            
            {/* Opening Hours */}
            <div className="mt-6 mb-4">
              <h4 className="text-lg font-medium mb-3">Opening Hours</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(formData.openingHours).map(([day, hours]) => (
                  <div key={day} className="flex items-center space-x-4 p-2 border rounded">
                    <div className="w-28">
                      <span className="font-medium capitalize">{day}</span>
                    </div>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={hours.isClosed}
                        onChange={() => handleOpeningHoursChange(day, 'isClosed')}
                        className="mr-2"
                      />
                      <span className="text-sm">Closed</span>
                    </label>
                    
                    <div className="flex-1 flex space-x-2">
                      <input
                        type="time"
                        value={hours.open}
                        onChange={(e) => handleOpeningHoursChange(day, 'open', e.target.value)}
                        disabled={hours.isClosed}
                        className={`w-full px-2 py-1 border rounded-md ${hours.isClosed ? 'bg-gray-100' : ''}`}
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="time"
                        value={hours.close}
                        onChange={(e) => handleOpeningHoursChange(day, 'close', e.target.value)}
                        disabled={hours.isClosed}
                        className={`w-full px-2 py-1 border rounded-md ${hours.isClosed ? 'bg-gray-100' : ''}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowAddForm(false);
                }}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                disabled={loading}
              >
                {loading ? 'Saving...' : editingPractice ? 'Update Practice' : 'Add Practice'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Practices List */}
      <div className="overflow-x-auto">
        {loading && !showAddForm ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : practices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No practices found. Add a practice to get started.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Practice Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Services
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Available Appointment
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {practices.map((practice) => (
                <tr key={practice.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{practice.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{practice.address.line1}</div>
                    {practice.address.line2 && (
                      <div className="text-sm text-gray-900">{practice.address.line2}</div>
                    )}
                    <div className="text-sm text-gray-900">
                      {practice.address.city}, {practice.address.postcode}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{practice.contact.phone}</div>
                    <div className="text-sm text-gray-900">{practice.contact.email}</div>
                    {practice.contact.website && (
                      <div className="text-sm text-blue-600 hover:underline">
                        <a href={practice.contact.website} target="_blank" rel="noopener noreferrer">
                          Website
                        </a>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {practice.services && practice.services.length > 0
                        ? practice.services.join(', ')
                        : 'No services listed'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {practiceAvailability[practice.id] ? (
                      <div>
                        <div className="text-sm font-medium text-green-600">
                          {formatDate(practiceAvailability[practice.id].date)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatTime(practiceAvailability[practice.id].startTime)} - {formatTime(practiceAvailability[practice.id].endTime)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No availability in next 7 days</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(practice)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(practice.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Import Modal */}
      {importModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h3 className="text-xl font-medium mb-4">Import Practices</h3>
            <p className="text-gray-600 mb-4">
              Paste JSON data for practices to import. The data should be an array of practice objects.
            </p>
            <textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              className="w-full h-64 px-3 py-2 border rounded-md mb-4"
              placeholder='[{"name": "Practice Name", "address": {"line1": "123 Main St", "city": "London", "postcode": "SW1A 1AA"}, "contact": {"phone": "020 1234 5678", "email": "info@practice.com"}, "services": ["Checkup", "Cleaning"]}]'
            ></textarea>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setImportModalOpen(false)}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                disabled={loading}
              >
                {loading ? 'Importing...' : 'Import Practices'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeManagement;
